<template>
  <div class="overtime-analytics">
    <!-- Header -->
    <div class="flex justify-end items-center mb-6">
      <div class="flex gap-4">
        <select v-model="selectedPeriod" class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="year">This Year</option>
        </select>
        <button
          @click="exportOvertimeReport"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Download class="w-4 h-4" />
          Export Report
        </button>
      </div>
    </div>

    <!-- Key Metrics -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Total Overtime Hours</p>
            <p class="text-3xl font-bold text-gray-900">{{ analytics.totalHours }}</p>
            <p class="text-xs text-gray-500 mt-1">{{ analytics.totalSessions }} sessions</p>
          </div>
          <Clock class="w-8 h-8 text-orange-500" />
        </div>
        <div class="mt-2 flex items-center">
          <span :class="analytics.hoursTrend > 0 ? 'text-red-600' : 'text-green-600'" class="text-sm font-medium">
            {{ analytics.hoursTrend > 0 ? '+' : '' }}{{ analytics.hoursTrend }}%
          </span>
          <span class="text-gray-500 text-sm ml-1">vs last period</span>
        </div>
      </div>

      <div class="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Overtime Cost</p>
            <p class="text-3xl font-bold text-gray-900">{{ formatCurrency(analytics.totalCost) }}</p>
            <p class="text-xs text-gray-500 mt-1">Avg. {{ formatCurrency(analytics.avgCostPerHour) }}/hr</p>
          </div>
          <DollarSign class="w-8 h-8 text-blue-500" />
        </div>
        <div class="mt-2 flex items-center">
          <span :class="analytics.costTrend > 0 ? 'text-red-600' : 'text-green-600'" class="text-sm font-medium">
            {{ analytics.costTrend > 0 ? '+' : '' }}{{ analytics.costTrend }}%
          </span>
          <span class="text-gray-500 text-sm ml-1">vs last period</span>
        </div>
      </div>

      <div class="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Approval Rate</p>
            <p class="text-3xl font-bold text-gray-900">{{ analytics.approvalRate }}%</p>
            <p class="text-xs text-gray-500 mt-1">{{ analytics.approvedRequests }}/{{ analytics.totalRequests }} requests</p>
          </div>
          <CheckCircle class="w-8 h-8 text-green-500" />
        </div>
      </div>

      <div class="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Avg. Daily Overtime</p>
            <p class="text-3xl font-bold text-gray-900">{{ analytics.avgDailyHours }}h</p>
            <p class="text-xs text-gray-500 mt-1">Per active employee</p>
          </div>
          <TrendingUp class="w-8 h-8 text-purple-500" />
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      <!-- Overtime Trend Chart -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4">Overtime Trend</h3>
        <div class="h-64">
          <canvas ref="overtimeTrendChart"></canvas>
        </div>
      </div>

      <!-- Department Breakdown -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4">Department Breakdown</h3>
        <div class="h-64">
          <canvas ref="departmentBreakdownChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Detailed Analysis -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
      <!-- Top Overtime Users -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users class="w-5 h-5 text-orange-500" />
          Top Overtime Users
        </h3>
        <div class="space-y-3">
          <div v-for="(user, index) in analytics.topUsers" :key="user.id" class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {{ index + 1 }}
              </div>
              <div>
                <p class="font-medium text-gray-900">{{ user.name }}</p>
                <p class="text-sm text-gray-500">{{ user.department }}</p>
              </div>
            </div>
            <div class="text-right">
              <p class="font-semibold text-orange-600">{{ user.overtimeHours }}h</p>
              <p class="text-xs text-gray-500">{{ formatCurrency(user.overtimeCost) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Cost by Department -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
          <Building class="w-5 h-5 text-blue-500" />
          Cost by Department
        </h3>
        <div class="space-y-4">
          <div v-for="dept in analytics.departmentCosts" :key="dept.id">
            <div class="flex justify-between text-sm mb-1">
              <span>{{ dept.name }}</span>
              <span class="font-medium">{{ formatCurrency(dept.cost) }}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div 
                class="bg-blue-600 h-2 rounded-full" 
                :style="{ width: (dept.cost / analytics.maxDepartmentCost * 100) + '%' }"
              ></div>
            </div>
            <div class="flex justify-between text-xs text-gray-500 mt-1">
              <span>{{ dept.hours }}h</span>
              <span>{{ dept.percentage }}% of total</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Request Patterns -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar class="w-5 h-5 text-purple-500" />
          Request Patterns
        </h3>
        <div class="space-y-4">
          <div>
            <h4 class="font-medium text-gray-700 mb-2">Peak Days</h4>
            <div class="space-y-2">
              <div v-for="day in analytics.peakDays" :key="day.day" class="flex justify-between text-sm">
                <span>{{ day.day }}</span>
                <span class="font-medium">{{ day.requests }} requests</span>
              </div>
            </div>
          </div>

          <div>
            <h4 class="font-medium text-gray-700 mb-2">Common Reasons</h4>
            <div class="space-y-2">
              <div v-for="reason in analytics.commonReasons" :key="reason.reason" class="flex justify-between text-sm">
                <span class="truncate">{{ reason.reason }}</span>
                <span class="font-medium">{{ reason.count }}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 class="font-medium text-gray-700 mb-2">Avg. Duration</h4>
            <div class="text-center">
              <p class="text-2xl font-bold text-purple-600">{{ analytics.avgDuration }}h</p>
              <p class="text-sm text-gray-500">Per session</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Policy Compliance -->
    <div class="bg-white p-6 rounded-lg shadow mb-8">
      <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
        <Shield class="w-5 h-5 text-green-500" />
        Policy Compliance
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div class="text-center">
          <p class="text-3xl font-bold text-green-600">{{ analytics.compliance.withinPolicy }}%</p>
          <p class="text-sm text-gray-600">Within Policy</p>
        </div>
        <div class="text-center">
          <p class="text-3xl font-bold text-yellow-600">{{ analytics.compliance.dailyViolations }}</p>
          <p class="text-sm text-gray-600">Daily Limit Violations</p>
        </div>
        <div class="text-center">
          <p class="text-3xl font-bold text-orange-600">{{ analytics.compliance.weeklyViolations }}</p>
          <p class="text-sm text-gray-600">Weekly Limit Violations</p>
        </div>
        <div class="text-center">
          <p class="text-3xl font-bold text-blue-600">{{ analytics.compliance.avgApprovalTime }}h</p>
          <p class="text-sm text-gray-600">Avg. Approval Time</p>
        </div>
      </div>
    </div>

    <!-- Cost Projection -->
    <div class="bg-white p-6 rounded-lg shadow">
      <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
        <TrendingUp class="w-5 h-5 text-blue-500" />
        Cost Projection
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="bg-blue-50 p-4 rounded-lg text-center">
          <p class="text-2xl font-bold text-blue-600">{{ formatCurrency(analytics.projection.monthly) }}</p>
          <p class="text-sm text-blue-800">Projected Monthly Cost</p>
          <p class="text-xs text-blue-600 mt-1">Based on current trend</p>
        </div>
        <div class="bg-purple-50 p-4 rounded-lg text-center">
          <p class="text-2xl font-bold text-purple-600">{{ formatCurrency(analytics.projection.quarterly) }}</p>
          <p class="text-sm text-purple-800">Projected Quarterly Cost</p>
          <p class="text-xs text-purple-600 mt-1">Q{{ getCurrentQuarter() }} estimate</p>
        </div>
        <div class="bg-green-50 p-4 rounded-lg text-center">
          <p class="text-2xl font-bold text-green-600">{{ formatCurrency(analytics.projection.savings) }}</p>
          <p class="text-sm text-green-800">Potential Savings</p>
          <p class="text-xs text-green-600 mt-1">With policy optimization</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import {
  Clock,
  DollarSign,
  CheckCircle,
  TrendingUp,
  Users,
  Building,
  Calendar,
  Shield,
  Download
} from 'lucide-vue-next';
import Chart from 'chart.js/auto';
import dayjs from 'dayjs';

// Import services
import attendanceService from '@/services/attendance';
import adminService from '@/services/adminService';
import { useCurrency } from '@/composables/useCurrency';

const { formatCurrency, getCurrencySymbol, loadCurrency } = useCurrency();

// Reactive data
const selectedPeriod = ref('30d');
const analytics = ref({
  totalHours: 0,
  totalSessions: 0,
  hoursTrend: 0,
  totalCost: 0,
  avgCostPerHour: 0,
  costTrend: 0,
  approvalRate: 0,
  approvedRequests: 0,
  totalRequests: 0,
  avgDailyHours: 0,
  maxDepartmentCost: 0,
  topUsers: [],
  departmentCosts: [],
  peakDays: [],
  commonReasons: [],
  avgDuration: 0,
  compliance: {
    withinPolicy: 0,
    dailyViolations: 0,
    weeklyViolations: 0,
    avgApprovalTime: 0
  },
  projection: {
    monthly: 0,
    quarterly: 0,
    savings: 0
  }
});

// Chart refs
const overtimeTrendChart = ref(null);
const departmentBreakdownChart = ref(null);

// Chart instances
let trendChart = null;
let breakdownChart = null;

// Methods
const loadAnalytics = async () => {
  try {
    console.log('📊 Loading overtime analytics for period:', selectedPeriod.value);
    
    const response = await attendanceService.overtime.admin.getOvertimeAnalytics({
      period: selectedPeriod.value
    });

    if (response.data) {
      analytics.value = { ...analytics.value, ...response.data };
      console.log('✅ Overtime analytics loaded:', analytics.value);
    } else {
      console.log('⚠️ No analytics data received, using mock data');
      loadMockData();
    }
  } catch (error) {
    console.error('❌ Error loading overtime analytics:', error);
    console.log('🔄 Using mock data for development');
    loadMockData();
  }
};

const loadMockData = () => {
  analytics.value = {
    totalHours: 1247,
    totalSessions: 89,
    hoursTrend: -5.2,
    totalCost: 74820,
    avgCostPerHour: 60,
    costTrend: -3.1,
    approvalRate: 92,
    approvedRequests: 82,
    totalRequests: 89,
    avgDailyHours: 2.3,
    maxDepartmentCost: 25000,
    topUsers: [
      { id: 1, name: 'John Smith', department: 'Engineering', overtimeHours: 45, overtimeCost: 2700 },
      { id: 2, name: 'Sarah Johnson', department: 'Operations', overtimeHours: 38, overtimeCost: 2280 },
      { id: 3, name: 'Mike Davis', department: 'Sales', overtimeHours: 32, overtimeCost: 1920 }
    ],
    departmentCosts: [
      { id: 1, name: 'Engineering', cost: 25000, hours: 416, percentage: 33 },
      { id: 2, name: 'Operations', cost: 20000, hours: 333, percentage: 27 },
      { id: 3, name: 'Sales', cost: 15000, hours: 250, percentage: 20 },
      { id: 4, name: 'Marketing', cost: 10000, hours: 167, percentage: 13 }
    ],
    peakDays: [
      { day: 'Monday', requests: 23 },
      { day: 'Friday', requests: 19 },
      { day: 'Thursday', requests: 15 }
    ],
    commonReasons: [
      { reason: 'Project deadline', count: 34 },
      { reason: 'Production issue', count: 21 },
      { reason: 'Client meeting', count: 18 }
    ],
    avgDuration: 2.8,
    compliance: {
      withinPolicy: 88,
      dailyViolations: 8,
      weeklyViolations: 3,
      avgApprovalTime: 1.2
    },
    projection: {
      monthly: 95000,
      quarterly: 285000,
      savings: 15000
    }
  };
};

const initializeCharts = async () => {
  await nextTick();
  
  // Overtime Trend Chart
  if (overtimeTrendChart.value) {
    trendChart = new Chart(overtimeTrendChart.value, {
      type: 'line',
      data: {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
          {
            label: 'Overtime Hours',
            data: [45, 52, 38, 41],
            borderColor: 'rgb(249, 115, 22)',
            backgroundColor: 'rgba(249, 115, 22, 0.1)',
            tension: 0.4
          },
          {
            label: `Overtime Cost (${getCurrencySymbol()}000)`,
            data: [2.7, 3.1, 2.3, 2.5],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Hours'
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: `Cost (${getCurrencySymbol()}000)`
            },
            grid: {
              drawOnChartArea: false,
            },
          }
        }
      }
    });
  }

  // Department Breakdown Chart
  if (departmentBreakdownChart.value) {
    breakdownChart = new Chart(departmentBreakdownChart.value, {
      type: 'doughnut',
      data: {
        labels: analytics.value.departmentCosts.map(dept => dept.name),
        datasets: [{
          data: analytics.value.departmentCosts.map(dept => dept.cost),
          backgroundColor: [
            'rgb(249, 115, 22)',
            'rgb(59, 130, 246)',
            'rgb(16, 185, 129)',
            'rgb(245, 158, 11)',
            'rgb(139, 92, 246)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    });
  }
};

const exportOvertimeReport = async () => {
  try {
    console.log('📤 Exporting overtime analytics report...');
    
    const response = await attendanceService.overtime.admin.exportOvertimeReport({
      period: selectedPeriod.value,
      format: 'xlsx',
      type: 'analytics'
    });

    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `overtime-analytics-${selectedPeriod.value}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Overtime analytics report exported successfully');
    showToast('success', 'Overtime analytics report exported successfully!');
  } catch (error) {
    console.error('❌ Error exporting report:', error);
    showToast('error', 'Failed to export report. Please try again.');
  }
};

const getCurrentQuarter = () => {
  return Math.ceil((dayjs().month() + 1) / 3);
};

// Toast notification helper
const showToast = (type, message) => {
  if (type === 'success') {
    console.log('✅', message);
  } else {
    console.error('❌', message);
  }
  // Simple alert for now - could be enhanced with proper toast component
  alert(message);
};

// Watchers
watch(selectedPeriod, () => {
  console.log('🔄 Period changed to:', selectedPeriod.value);
  loadAnalytics();
});

// Lifecycle
onMounted(() => {
  console.log('🔄 OvertimeAnalytics mounted, loading data...');
  loadCurrency();
  loadAnalytics();
  initializeCharts();
});
</script>

<style scoped>
.overtime-analytics {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}
</style>